<template>
  <section class="w-full py-16 md:py-20 lg:py-24 bg-white">
    <div class="max-w-[1440px] mx-auto px-6 md:px-8 lg:px-12">
      <!-- Header Section -->
      <div class="text-center mb-12 md:mb-16">
        <!-- Title with blue dot -->
        <div class="flex items-center justify-center gap-3 mb-4">
          <div class="w-3 h-3 rounded-full bg-[#1A4666]"></div>
                   <!-- Titre - Mobile -->
          <H3Title className="md:hidden text-2xl sm:text-3xl font-extrabold text-[#0F7F5C] uppercase tracking-tight">
            COMMENT FAIRE UN SIGNALEMENT ?
          </H3Title>
          
          <!-- Titre - Desktop -->
          <H2Title className="hidden md:block text-3xl md:text-4xl lg:text-5xl font-extrabold text-[#0F7F5C] uppercase tracking-tight">
            COMMENT FAIRE UN SIGNALEMENT ?
          </H2Title>
        </div>
        
        <!-- Subtitle - Mobile -->
        <PContent className="md:hidden text-sm sm:text-base text-[#6F7378] font-normal max-w-2xl mx-auto">
          Découvrez le processus simple et sécurisé pour soumettre votre signalement
        </PContent>
        
        <!-- Subtitle - Desktop -->
        <H4Title className="hidden md:block text-base md:text-lg lg:text-xl text-[#6F7378] font-normal max-w-2xl mx-auto">
          Découvrez le processus simple et sécurisé pour soumettre votre signalement
        </H4Title>
      </div>
      <!-- Two Columns Layout -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 lg:gap-16 items-start">
        <!-- Left Column: Video Player -->
        <div ref="videoContainerRef" class="w-full opacity-0">
          <div class="bg-gradient-to-br from-gray-200 to-gray-300 rounded-2xl shadow-lg overflow-hidden aspect-video relative flex items-center justify-center">
            <!-- Real Video Element -->
            <video 
              ref="videoRef"
              class="absolute inset-0 w-full h-full object-cover cursor-pointer"
              loop
              muted
              playsinline
              poster=""
              @click="togglePlay"
              @error="handleVideoError"
            >
              <source src="/videos/landingpage/how-it-works.mp4" type="video/mp4">
            </video>

            <!-- Play Button Overlay -->
            <div 
              v-if="!isPlaying"
              class="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-black/10 cursor-pointer group"
              @click="togglePlay"
            >
              <button class="w-20 h-20 md:w-24 md:h-24 rounded-full bg-[#1A4666] flex items-center justify-center shadow-xl hover:bg-[#0A2F70] transition-colors duration-300 group-hover:scale-105">
                <Play class="w-10 h-10 md:w-12 md:h-12 text-white ml-1 fill-white" />
              </button>
              
              <!-- Video Info Text -->
              <div class="text-center">
                <p class="text-white font-medium text-base md:text-lg mb-1 drop-shadow-md">
                  Vidéo de démonstration
                </p>
                <p class="text-white/80 text-sm md:text-base drop-shadow-sm">
                  Durée: 2:00
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Right Column: Steps Timeline -->
        <div ref="stepsContainerRef" class="w-full opacity-0">
          <div class="relative">
            <!-- Vertical Line -->
            <div
              ref="timelineRef"
              class="absolute left-6 top-0 bottom-0 w-1 hidden lg:block"
            >
              <div class="absolute inset-0 bg-[#D7E6DE]"></div>
              <div
                class="absolute top-0 left-0 w-full bg-[#0F7F5C] transition-[height] duration-300 ease-out"
                :style="{ height: `${lineProgress}%` }"
              ></div>
            </div>
            
            <!-- Steps List -->
            <div class="space-y-8 md:space-y-10">
              <div 
                v-for="(step, index) in steps" 
                :key="index"
                class="relative flex items-start gap-4 md:gap-6 transition-opacity duration-300"
                :class="[ activeStep >= index + 1 ? 'opacity-100' : 'opacity-50' ]"
              >
                <div class="relative z-10 shrink-0">
                  <div 
                    class="w-12 h-12 md:w-14 md:h-14 rounded-full flex items-center justify-center transition-colors duration-500"
                    :class="[ activeStep >= index + 1 ? 'bg-[#0F7F5C]' : 'bg-[#E4EBF3]' ]"
                  >
                    <component 
                      :is="step.icon" 
                      class="w-6 h-6 md:w-7 md:h-7 transition-colors duration-500" 
                      :class="[ activeStep >= index + 1 ? 'text-white' : 'text-[#1A4666]' ]"
                    />
                  </div>
                </div>
                <div class="flex-1 pt-1">
                  <h3 
                    class="text-lg md:text-xl font-semibold transition-colors duration-500"
                    :class="[ activeStep >= index + 1 ? 'text-[#0F7F5C]' : 'text-[#1A4666]' ]"
                  >
                    {{ step.title }}
                  </h3>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { gsap } from 'gsap'
import { onBeforeUnmount, onMounted, ref } from 'vue'
import { H2Title, H3Title, H4Title, PContent } from '@/components/atoms/text'
import { Play, User, Check, FileText, Send } from 'lucide-vue-next'

defineOptions({
  name: 'HowItWorkSection'
})

const steps = [
  {
    title: 'Identification du déclarant',
    icon: User
  },
  {
    title: 'Description de l\'irrégularité',
    icon: Check
  },
  {
    title: 'Relire et Transmettre',
    icon: FileText
  },
  {
    title: 'Suivi du Signalement',
    icon: Send
  }
]

const videoContainerRef = ref<HTMLElement | null>(null)
const stepsContainerRef = ref<HTMLElement | null>(null)
const timelineRef = ref<HTMLElement | null>(null)
const videoRef = ref<HTMLVideoElement | null>(null)
const lineProgress = ref(0)
const activeStep = ref(0)
const isPlaying = ref(false)

const handleVideoError = () => {
  console.error("Video failed to load correctly")
  // Fallback to a static image or a different source if needed
}

const togglePlay = async () => {
  if (!videoRef.value) return
  try {
    if (videoRef.value.paused) {
      await videoRef.value.play()
      isPlaying.value = true
    } else {
      videoRef.value.pause()
      isPlaying.value = false
    }
  } catch (err) {
    console.error("Video playback failed:", err)
    isPlaying.value = false
  }
}

const updateLineProgress = () => {
  const timeline = timelineRef.value
  if (!timeline) return

  const rect = timeline.getBoundingClientRect()
  const viewportHeight = window.innerHeight

  // Progress starts when timeline enters viewport center and ends near viewport top
  const start = viewportHeight * 0.8
  const end = viewportHeight * 0.2
  const traveled = start - rect.top
  const total = rect.height
  const progress = Math.min(1, Math.max(0, traveled / total))

  lineProgress.value = progress * 100
  
  // Update active step based on progress
  if (progress > 0.85) activeStep.value = 4
  else if (progress > 0.6) activeStep.value = 3
  else if (progress > 0.35) activeStep.value = 2
  else if (progress > 0.05) activeStep.value = 1
  else activeStep.value = 0
}

onMounted(() => {
  // GSAP Entrance Animations (Subtle)
  gsap.to(videoContainerRef.value, {
    opacity: 1,
    duration: 1,
    ease: 'power2.out',
    delay: 0.1
  })

  gsap.to(stepsContainerRef.value, {
    opacity: 1,
    duration: 1,
    ease: 'power2.out',
    delay: 0.2
  })

  updateLineProgress()
  window.addEventListener('scroll', updateLineProgress, { passive: true })
  window.addEventListener('resize', updateLineProgress)
})

onBeforeUnmount(() => {
  window.removeEventListener('scroll', updateLineProgress)
  window.removeEventListener('resize', updateLineProgress)
})
</script>

<style scoped>
@keyframes pulse-soft {
  0%, 100% { opacity: 0.2; transform: scale(1); }
  50% { opacity: 0.4; transform: scale(1.05); }
}

.timeline-glow {
  filter: drop-shadow(0 0 8px rgba(15, 127, 92, 0.4));
}
</style>

